import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where, deleteDoc, doc } from 'firebase/firestore';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Chip, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

const OwnersTable = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteOwnerTarget, setDeleteOwnerTarget] = useState(null);

  const fetchOwners = async () => {
    setLoading(true);
    const q = query(collection(db, 'registrations'), orderBy('name'));
    const querySnapshot = await getDocs(q);
    const ownerMap = {};

    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const ownerKey = `${data.name}-${data.contact}-${data.whatsapp}`;
      if (!ownerMap[ownerKey]) {
        ownerMap[ownerKey] = {
          id: ownerKey,
          name: data.name,
          contact: data.contact,
          whatsapp: data.whatsapp,
          vehicles: [],
          relations: [],
        };
      }

      ownerMap[ownerKey].vehicles.push(data.vehicleNumber || '-');
      const emergencyRelations = data.emergencyContacts?.map(ec => ec.relation).filter(Boolean) || [];
      ownerMap[ownerKey].relations = Array.from(new Set([...ownerMap[ownerKey].relations, ...emergencyRelations]));
    });

    setOwners(Object.values(ownerMap));
    setLoading(false);
  };

  useEffect(() => {
    fetchOwners();
  }, []);

  const handleDelete = (owner) => {
    setDeleteOwnerTarget(owner);
  };

  const confirmDelete = async () => {
    if (!deleteOwnerTarget) return;

    const q = query(
      collection(db, 'registrations'),
      where('name', '==', deleteOwnerTarget.name),
      where('contact', '==', deleteOwnerTarget.contact),
      where('whatsapp', '==', deleteOwnerTarget.whatsapp)
    );

    const snapshot = await getDocs(q);
    const batchDeletes = snapshot.docs.map(item => deleteDoc(doc(db, 'registrations', item.id)));
    await Promise.all(batchDeletes);
    setDeleteOwnerTarget(null);
    fetchOwners();
  };

  const cancelDelete = () => {
    setDeleteOwnerTarget(null);
  };

  if (loading) return <Typography>Loading owners...</Typography>;
  if (!owners.length) return <Typography>No owners found.</Typography>;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Owner Directory</Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)', fontWeight: 700 }}>Owner</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)', fontWeight: 700 }}>Contact</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)', fontWeight: 700 }}>WhatsApp</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)', fontWeight: 700 }}>Vehicles</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)', fontWeight: 700 }}>Emergency Relations</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)', fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {owners.map(owner => (
              <TableRow key={owner.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell>{owner.name}</TableCell>
                <TableCell>{owner.contact}</TableCell>
                <TableCell>{owner.whatsapp}</TableCell>
                <TableCell>{owner.vehicles.join(', ')}</TableCell>
                <TableCell>
                  {owner.relations.length ? owner.relations.map(rel => (
                    <Chip key={rel} label={rel} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  )) : '-'}
                </TableCell>
                <TableCell>
                  <Button color="error" size="small" variant="outlined" onClick={() => handleDelete(owner)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={Boolean(deleteOwnerTarget)} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete all registrations for this owner? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnersTable;
